import { useState, useEffect } from "react";
import { 
  MessageSquare, 
  Plus, 
  Pin, 
  AlertTriangle, 
  AlertCircle,
  Send,
  Trash2,
  Edit2,
  Clock,
  User,
  X,
  CheckCircle
} from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const NOTE_TYPES = {
  normal: { 
    label: "Normal", 
    icon: MessageSquare, 
    color: "text-slate-600", 
    bg: "bg-slate-50",
    border: "border-slate-200"
  },
  important: { 
    label: "Importante", 
    icon: Pin, 
    color: "text-blue-600", 
    bg: "bg-blue-50",
    border: "border-blue-200"
  },
  alert: { 
    label: "Alerta", 
    icon: AlertTriangle, 
    color: "text-red-600", 
    bg: "bg-red-50",
    border: "border-red-200"
  }
};

const CaseNotesSection = ({ caseId, className = "" }) => {
  const { user, token } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newNote, setNewNote] = useState({
    content: "",
    note_type: "normal",
    pinned: false
  });
  const [editingNote, setEditingNote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (caseId) {
      fetchNotes();
      fetchSummary();
    }
  }, [caseId]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/api/case-notes/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/api/case-notes/case/${caseId}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.content.trim()) return;

    setSubmitting(true);
    try {
      if (editingNote) {
        await axios.put(`${API}/api/case-notes/${editingNote.id}`, {
          content: newNote.content,
          note_type: newNote.note_type,
          pinned: newNote.pinned
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/api/case-notes`, {
          case_id: caseId,
          content: newNote.content,
          note_type: newNote.note_type,
          pinned: newNote.pinned
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setNewNote({ content: "", note_type: "normal", pinned: false });
      setShowForm(false);
      setEditingNote(null);
      fetchNotes();
      fetchSummary();
    } catch (error) {
      console.error("Error saving note:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setNewNote({
      content: note.content,
      note_type: note.note_type,
      pinned: note.pinned
    });
    setShowForm(true);
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm("¿Eliminar esta nota?")) return;
    
    try {
      await axios.delete(`${API}/api/case-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotes();
      fetchSummary();
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const cancelEdit = () => {
    setEditingNote(null);
    setNewNote({ content: "", note_type: "normal", pinned: false });
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return "Hace un momento";
    if (hours < 24) return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
    if (days < 7) return `Hace ${days} día${days > 1 ? 's' : ''}`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'oficial': return 'bg-blue-100 text-blue-700';
      case 'vendedor': return 'bg-emerald-100 text-emerald-700';
      case 'asesor': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-navy-100 p-6 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-navy-400" />
          <h3 className="font-semibold text-navy-900">Notas Internas</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-20 bg-slate-100 rounded-lg"></div>
          <div className="h-20 bg-slate-100 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-navy-100 ${className}`} data-testid="case-notes-section">
      {/* Header */}
      <div className="p-4 border-b border-navy-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-navy-900">Notas Internas</h3>
              <p className="text-xs text-navy-500">Solo visible para el equipo</p>
            </div>
          </div>
          <Button 
            size="sm" 
            onClick={() => setShowForm(!showForm)}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="add-note-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nueva Nota
          </Button>
        </div>

        {/* Summary badges */}
        {summary && summary.total > 0 && (
          <div className="flex gap-2 mt-3">
            {summary.by_type.alert > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs">
                <AlertTriangle className="w-3 h-3" />
                {summary.by_type.alert} alerta{summary.by_type.alert > 1 ? 's' : ''}
              </span>
            )}
            {summary.by_type.important > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
                <Pin className="w-3 h-3" />
                {summary.by_type.important} importante{summary.by_type.important > 1 ? 's' : ''}
              </span>
            )}
            {summary.pinned > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs">
                <Pin className="w-3 h-3" />
                {summary.pinned} fijada{summary.pinned > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* New Note Form */}
      {showForm && (
        <div className="p-4 bg-slate-50 border-b border-navy-100">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <textarea
                value={newNote.content}
                onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                placeholder="Escribe tu nota aquí..."
                rows={3}
                className="w-full px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                data-testid="note-content-input"
              />
            </div>
            
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                {/* Note Type Selector */}
                <div className="flex items-center gap-1 bg-white rounded-lg border border-navy-200 p-1">
                  {Object.entries(NOTE_TYPES).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setNewNote({...newNote, note_type: key})}
                        className={`p-2 rounded-md transition-colors ${
                          newNote.note_type === key 
                            ? `${config.bg} ${config.color}` 
                            : 'text-slate-400 hover:text-slate-600'
                        }`}
                        title={config.label}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>

                {/* Pin Toggle */}
                <button
                  type="button"
                  onClick={() => setNewNote({...newNote, pinned: !newNote.pinned})}
                  className={`p-2 rounded-lg border transition-colors ${
                    newNote.pinned 
                      ? 'bg-amber-100 border-amber-300 text-amber-600' 
                      : 'bg-white border-navy-200 text-slate-400 hover:text-slate-600'
                  }`}
                  title={newNote.pinned ? "Nota fijada" : "Fijar nota"}
                >
                  <Pin className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                {editingNote && (
                  <Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
                    Cancelar
                  </Button>
                )}
                <Button 
                  type="submit" 
                  size="sm" 
                  disabled={!newNote.content.trim() || submitting}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="submit-note-btn"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-1" />
                      {editingNote ? "Actualizar" : "Guardar"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Notes List */}
      <div className="max-h-96 overflow-y-auto">
        {notes.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-navy-500">No hay notas todavía</p>
            <p className="text-sm text-navy-400">Las notas internas ayudan al equipo a coordinarse</p>
          </div>
        ) : (
          <div className="divide-y divide-navy-100">
            {notes.map((note) => {
              const noteConfig = NOTE_TYPES[note.note_type] || NOTE_TYPES.normal;
              const NoteIcon = noteConfig.icon;
              const canModify = note.author_id === user?.id || user?.role === 'admin';
              
              return (
                <div 
                  key={note.id} 
                  className={`p-4 ${note.pinned ? 'bg-amber-50/50' : ''}`}
                  data-testid={`note-${note.id}`}
                >
                  {/* Note Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full ${noteConfig.bg} flex items-center justify-center`}>
                        <NoteIcon className={`w-4 h-4 ${noteConfig.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-navy-900 text-sm">{note.author_name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getRoleBadgeColor(note.author_role)}`}>
                            {note.author_role}
                          </span>
                          {note.pinned && (
                            <Pin className="w-3 h-3 text-amber-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-navy-400">
                          <Clock className="w-3 h-3" />
                          {formatDate(note.created_at)}
                          {note.updated_at && (
                            <span className="text-navy-300">(editada)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {canModify && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(note)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Note Content */}
                  <div className={`ml-10 p-3 rounded-lg ${noteConfig.bg} ${noteConfig.border} border`}>
                    <p className="text-navy-700 text-sm whitespace-pre-wrap">{note.content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseNotesSection;
